import { createSelector, createStructuredSelector } from 'reselect';
import config from 'config';
import FeatureToggleUtils from 'FeatureToggleUtils';

import { loginTypes } from '../constants/Enums';
import { eventSelector, getRegistrationsUrl } from './EventSelectors';
import { hasWhitelabelVenue } from '../utils/RingEmbedScript';

const loginSettingsSelector = state => state.loginReducer;

const loginTypeSelector = state => state.loginReducer.loginType;

export const ssoAccountIdSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.ssoAccountConfigId
);

// Resolved server-side from the invite-list-gate-headline LaunchDarkly flag,
// so this is already the configured value or the default - never empty.
export const inviteListHeadlineSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.inviteListHeadline
);

// Sanitised server-side in GateTheme; travels on to the login iframe, which
// validates it again on arrival.
export const inviteListFontFamilySelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.inviteListFontFamily
);

export const enableLoginRegistrationPopUpSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.enableLoginRegistrationPopUp
);

export const isLoginShownSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.isLoginShown
);

export const offsetTopSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.offsetTop
);

export const loginLoadingStateSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => ({
        isLoading: loginSettings.isLoading,
        spinnerOrigin: loginSettings.spinnerOrigin
    })
);

export const enableTicketIdLoginSelector = createSelector(
    loginTypeSelector,
    (loginType) => loginTypes.EMAIL_AND_TICKET_ID === loginType
);

export const isSSOLoginSelector = createSelector(
    loginTypeSelector,
    (loginType) => loginTypes.SSO === loginType
);

export const isInviteListLoginSelector = createSelector(
    loginTypeSelector,
    (loginType) => loginTypes.EMAIL_INVITE_LIST === loginType
);

export const getRedirectRingUrl = createSelector(
    eventSelector,
    state => state.ringReducer,
    (event, { available, enabled }) => {
        const isRingFeatureEnabled = FeatureToggleUtils.isFeatureEnabled('ring');
        const isRingWorks = isRingFeatureEnabled && available && enabled;

        if (!isRingWorks) {
            return null;
        }

        if (hasWhitelabelVenue(event)) {
            if (!event.onlyEventDomain) {
                return `https://${event.whitelabelUrl}${event.whitelabelPath}/venue`;
            }
            return `https://${event.whitelabelUrl}/venue`;
        } else if (config.ringUrl) {
            const path = event['unique-name'];
            return `${config.ringUrl}/${path}`;
        }

        return null;
    }
);

export const isSsoEmailErrorModalVisibleSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.showSsoModal
);

export const ssoErrorEmailSelector = createSelector(
    loginSettingsSelector,
    (loginSettings) => loginSettings.errorEmail
);

export const ssoEmailErrorModalSelector = createStructuredSelector({
    showSsoErrorModal: isSsoEmailErrorModalVisibleSelector,
    errorEmail: ssoErrorEmailSelector,
    registrationsUrl: getRegistrationsUrl,
});

export const cookieConsentSrcSelector = createSelector(
    loginSettingsSelector,
    loginSettings => loginSettings?.cookieConsentSrc || false
);
